import React, { useState, useEffect, useCallback, useRef } from 'react'

// Deps
import { connect } from 'react-redux'
import compareAsc from 'date-fns/compareAsc'
import addYears from 'date-fns/addYears'
import startOfToday from 'date-fns/startOfToday'
import endOfDay from 'date-fns/endOfDay'
import formatISO from 'date-fns/formatISO'
import debounce from 'lodash/debounce'
import pick from 'lodash/pick'
import { redirect } from 'controllers/navigator';
import addDays from 'date-fns/addDays';
import dayjs from "dayjs"

// Functions
import formatNumber from 'functions/format-number'
import formatDate from 'functions/format-date'
import normalizePhone from 'functions/normalize-phone'
import omit from 'lodash/omit'

// Utils
import { openModal } from 'utils/modals';

// Partials
import Btn from 'views/shared/partials/btn'
import Collapser from 'views/shared/partials/collapser'
import { InputForm, FormInput } from 'views/shared/partials/forms'
import Loader from 'views/shared/partials/loader'
import { Table, TR, TD } from 'views/shared/partials/table'
import Pricebox from 'views/shared/partials/pricebox'

// Services
import orderServices from 'services/order'
import companyServices from 'services/company'
import commerceServices from 'services/commerce'

// Data
import invoiceTypes from 'data/invoice-types'
import currencyTypes from 'data/currency-types'
// import customOrderTypes from 'data/custom-order-types'
import orderPlanPeriodTimes from 'data/order-plan-period-times'

// Hooks
import useGroups from 'hooks/groups'
import useCities from 'hooks/cities'
import useDistricts from 'hooks/districts'

// Consts
const today = startOfToday();
const defaultExchangeType = null; // { value: 0, label: 'Deaktif'};
const defaultCompanyPaymentType = { value: 0, label: 'Otomatik'};

const anonymousClientTitle = 'Anonim Müşteri';
const anonymousClient = {
	value: 'anonim',
	client_code: 'anonim',
	label: anonymousClientTitle,
	company_name: anonymousClientTitle,
};

const mapStateToProps = (state, ownProps) => {
	return {
		mobileState: state.site.mobile,
		routeBase: state.site.routeBase,
		userGroups: state.user.groups,
		userData: state.user.data,
	};
};

const useTitle = (template, productInfo, expressOrder) => {
	if(template) {
		return <React.Fragment>
			Yeni Ödeme Şablonu Oluştur <span className="label category-template">Ödeme Şablonları</span>
		</React.Fragment>;
	}
	else if(productInfo) {
		if(productInfo.method === 'address') {
			return 'Online Sipariş Oluştur';
		}
		else {
			return 'Yeni Sipariş Oluştur';
		}
	}
	else if(expressOrder) {
		return 'Hızlı Ödeme Gerçekleştir'
	}
	else {
		return 'Yeni Ödeme Talebi Oluştur';
	}
}

const useDeliveryInfo = (productInfo) => {
	if(productInfo && productInfo.method === 'address' && productInfo.deliveryDate) {
		const deliveryDate = addDays(new Date(), productInfo.deliveryDate);
		return <>En geç <strong>{formatDate(deliveryDate, 'd MMMM EEEE', { parsed: true })}</strong> günü kargoya verilir.</>;
	}
	else {
		return false;
	}
}

const useFilteredGroups = (groups, productInfo) => {
	const computeGroups = (groups, productInfo) => {
		if(productInfo) {
			return groups.filter((group) => {
				let hasStock = true;
				for(const product of productInfo.products) {
					const stockStore = product.store_stock.find(s => s.store_id === group.id);
					if(!(stockStore && stockStore.stock >= product.quantity)) {
						hasStock = false;
					}
				}
				return hasStock;
			});
		}
		else {
			return groups;
		}
	}

	const [filteredGroups, setFilteredGroups] = useState([])

	useEffect(() => {
		setFilteredGroups(computeGroups(groups, productInfo));
	}, [groups, productInfo])

	return filteredGroups;
}

const CreateOrderModal = ({routeBase, clientID, close, productInfo, expressOrder, ...props}) => {
	const _mounted = useRef(false);
	const formElement = useRef();

	const headingTitle = useTitle(props.template, productInfo, expressOrder);
	const deliveryTitle = useDeliveryInfo(productInfo);

	const [submitting, setSubmitting] = useState(false);
	const [activeClient, setActiveClient] = useState((props.orderPlan || productInfo) ? null : anonymousClient);
	const [userInfoTabOpen, setUserInfoTabOpen] = useState(productInfo ? true : false);
	const [invoiceInfoTabOpen, setInvoiceInfoTabOpen] = useState(false);

	const [selectedCity, setSelectedCity] = useState(null);
	const [selectedDistrict, setSelectedDistrict] = useState(null);

	const [selectedInvoiceCity, setSelectedInvoiceCity] = useState(null);
	const [selectedInvoiceDistrict, setSelectedInvoiceDistrict] = useState(null);

	// Payment Type Options
	const [activeCompanyPaymentType, setActiveCompanyPaymentType] = useState(defaultCompanyPaymentType);
	const [activeCompanyPaymentInstallment, setActiveCompanyPaymentInstallment] = useState(null);
	const [companyPaymentInstallmentSelectionEnabled, setCompanyPaymentInstallmentSelectionEnabled] = useState(false);

	const [orderInvoiceType, setOrderInvoiceType] = useState(null);
	const [orderStartDate, setOrderStartDate] = useState(today);
	const [orderDueDate, setOrderDueDate] = useState(routeBase === 'terminal' ? today : addYears(today, 1));
	const [orderTotal, setOrderTotal] = useState(productInfo ? productInfo.total : '');
	const [orderCurrency, setOrderCurrency] = useState(currencyTypes[0]);
	const [orderExchangeType, setOrderExchangeType] = useState(defaultExchangeType);
	const [orderCurrencyExchangeEnabled, setOrderCurrencyExchangeEnabled] = useState(false);

	// Order Plan
	const [orderPlanEnabled, setOrderPlanEnabled] = useState(props.orderPlan);
	const [showOrderPlanTab, setShowOrderPlanTab] = useState(props.orderPlan);
	const [orderPlanPeriodMethod, setOrderPlanPeriodMethod] = useState('static');
	const [orderPlanPeriodTimeRange, setOrderPlanPeriodTimeRange] = useState(1);
	const [orderPlanPeriodTime, setOrderPlanPeriodTime] = useState(orderPlanPeriodTimes[2]);
	const [orderPlanPeriodCount, setOrderPlanPeriodCount] = useState(2);
	const [orderPlanData, setOrderPlanData] = useState(false);
	const [orderPlanDataLoading, setOrderPlanDataLoading] = useState(false);

	// Address Info
	const [userAddressInfo, setUserAddressInfo] = useState({})
	const [userDataAddressImportAvailable, setUserDataAddressImportAvailable] = useState(false)
	const [addressInvoiceImportAvailable, setAddressInvoiceImportAvailable] = useState(false)

	const [exchangeTypes, setExchangeTypes] = useState(false);

	const [selectedGroup] = useState((productInfo !== false && productInfo.targetGroup) ? {...productInfo.targetGroup, label: productInfo.targetGroup.name, value: productInfo.targetGroup.store_code} : null);

	const filteredGroups = useFilteredGroups(props.userGroups, productInfo);
	const groups = useGroups(filteredGroups, false);
	const cities = useCities();
	const districts = useDistricts(selectedCity);
	const invoiceDistricts = useDistricts(selectedInvoiceCity);

	const searchClients = (search) => {
		return new Promise((resolve, reject) => {
			companyServices.listClients({ search_value: search }).then((payload) => {
				let results = payload.clients.filter((client) => (
					client.client_code !== 'anonim'
				)).map((client) => {
					return {...client, value: client.client_code, label: (client.company_name ? (client.company_name + (client.executive_name ? ' / ' : '')) : '') + client.executive_name + ' ' + client.executive_surname};
				})

				if (props.orderPlan === false && (search === '' || anonymousClientTitle.search(new RegExp(search, 'i')) > -1)) {
					results = [
						anonymousClient,
						...results
					];
				}

				resolve(results);
			}).catch(() => {
				reject(false);
			})
		})
	}

	const searchCompanyPaymentTypes = (search) => {
		return new Promise((resolve, reject) => {
			companyServices.listPaymentTypes({ search_value: search, per_page: 10 }).then((payload) => {
				let results = payload.paymentTypes.map((type) => {
					return {
						...type,
						value: type.id,
						label: type.api_name + ' (' + type.payment_type.name + ')',
					};
				})

				if (search === '' || defaultCompanyPaymentType.label.search(new RegExp(search, 'i')) > -1) {
					results = [
						defaultCompanyPaymentType,
						...results
					];
				}

				resolve(results);
			}).catch(() => {
				reject(false);
			})
		})
	}

	const searchCompanyPaymentInstallments = (typeID, search) => {
		return new Promise((resolve, reject) => {
			companyServices.listPaymentInstallments({ company_payment_type_id: typeID, search_value: search, per_page: 10 }).then((payload) => {
				let results = payload.installments.map((installment) => {
					return {
						...installment,
						value: installment.id,
						label: installment.installment_calculate + ' Taksit - ' + installment.description,
					};
				});

				resolve(results);
			}).catch(() => {
				reject(false);
			})
		})
	}

	const submitWithoutProducts = (formData) => {
		setSubmitting(true);

		let submitData = {
			...omit(
				formData,
				[
					'periodic_payment_enabled',
					'plan_period_method',
					'client_search'
				]
			),
			create_or_update_order: 'create',
			create_or_update_client: 'create',
		};

		if(selectedCity) {
			submitData.client_delivery_city = selectedCity.name;
		}
		if(selectedDistrict) {
			submitData.client_delivery_district = selectedDistrict.name;
		}
		if(selectedInvoiceCity) {
			submitData.client_invoice_city = selectedInvoiceCity.name;
		}
		if(selectedInvoiceDistrict) {
			submitData.client_invoice_district = selectedInvoiceDistrict.name;
		}

		if (!formData.order_currency_exchange_type_id) {
			submitData.order_currency_exchange_type_id = '0';
		}

		if (orderPlanEnabled && orderPlanPeriodMethod === 'dynamic') {
			submitData.order_period_count = 1;
		}

		if (activeClient) {
			submitData.create_or_update_client = 'update';
			submitData.client_code = activeClient.client_code;
		}

		if (props.template) {
			submitData.save_to_template = 1;
		}

		orderServices.expressOrder(submitData).then((payload) => {
			if (props.onSuccess) {
				props.onSuccess(payload);
			}

			if (expressOrder) {
				window.open(payload.order_url, '_blank').focus();
			}
			else if (props.template) {
				close();
			}
			else if (formData.order_start_date && today < orderStartDate) {
				if(payload.order_plan_id) {
					redirect('orderPlanDetail', { id: payload.order_plan_id })
				}
				else {
					redirect('orderDetail', { id: payload.order_business_code })
				}
				close();
			}
			else {
				openModal('order-summary', { orderCode: payload.order_business_code });
			}
		}).catch(() => {
		}).finally(() => {
			setSubmitting(false);
		});
	}

	const submitWithProducts = (formData) => {

		const createOrder = (formData) => {
			commerceServices.createOrder(formData).then((tokenPayload) => {
				if(tokenPayload.order_prova_token) {
					commerceServices.createOrder({
						order_prova_token: tokenPayload.order_prova_token
					}).then((payload) => {
						commerceServices.clearBasket();
						redirect('dashboard');
						close();
						openModal('order-summary', { orderCode: payload.order_business_code });
					}).finally(() => {
						setSubmitting(false);
					})
				}
			}).catch(() => {
				setSubmitting(false);
			})
		}

		setSubmitting(true);
		let productIds = [];

		for (const product of productInfo.products) {
			for(let n = 0; n < product.quantity; n++) {
				productIds.push(product.id);
			}
		}

		let submitData = {
			order_store_code: props.userData.default_group ? props.userData.default_group.store_code : null,
			order_target_store_code: formData.order_store_code,
			order_total_amount: formData.order_total_amount,
			order_price_type_id: formData.order_price_type_id,
			product_id: productIds,
			order_start_date: today,
			order_due_date: formatISO(endOfDay(today)).split('+')[0],
		}

		if(activeClient) {
			submitData.client_code = activeClient.client_code;
			createOrder(submitData);
		}
		else {
			let clientInfo = {
				...pick(formData, [
					'client_invoice_type',
					'client_custom_order',
					'client_executive_name',
					'client_executive_surname',
					'client_email',
					'client_phone_number',
					// Fatura Bilgileri
					'client_code',
					'client_tax_office',
					'client_tax_number',
					'client_company_name',
					'client_identity_number',
					'client_invoice_name',
					'client_invoice_address',
				]),
				'client_invoice_city': selectedInvoiceCity.label,
				'client_invoice_district': selectedInvoiceDistrict.label,
			};

			// clientInfo.client_invoice_city = selectedInvoiceCity.label;
			// clientInfo.client_invoice_district = selectedInvoiceDistrict.label;

			if(productInfo.requestAddress) {
				clientInfo = {
					...clientInfo,
					...pick(formData, [
						'client_delivery_phone',
						'client_delivery_owner',
						'client_delivery_title',
						'client_delivery_address',
					]),
					'client_delivery_city': selectedCity.label,
					'client_delivery_district': selectedDistrict.label,
				};
			}

			// console.log(clientInfo);
			// setSubmitting(false);

			if(orderInvoiceType === 1) {
				clientInfo.client_identity_number = '11111111111';
			}

			orderServices.expressOrder({ ...clientInfo, create_or_update_client: 'create' }).then((payload) => {
				submitData.order_start_date = dayjs().format('YYYY-MM-DDTHH:mm:ss')
				submitData.order_due_date = dayjs(submitData.order_start_date).add(1, 'days').format('YYYY-MM-DDTHH:mm:ss')
				createOrder({...submitData, client_code: payload.client_code});
			}).catch(() => {
				setSubmitting(false);
			})
		}
	}

	const validationChange = (invalidFields) => {
		let invoiceFieldInvalid = false;

		for(const key in invalidFields) {
			if(key.startsWith('client_invoice')) {
				invoiceFieldInvalid = true;
			}
		}

		if(invoiceFieldInvalid) {
			setInvoiceInfoTabOpen(true);
		}
	}

	const resetClient = () => {
		setActiveClient(null);
		setTimeout(() => { setUserInfoTabOpen(true); }, 50);
		setSelectedCity(null);
		setSelectedDistrict(null);
		setSelectedInvoiceCity(null);
		setSelectedInvoiceDistrict(null);
	}

	const updateActiveClient = () => {
		if (activeClient.client_code !== 'anonim') {
			companyServices.getClient(activeClient.client_code).then((clientInfo) => {
				setActiveClient({
					...clientInfo,
					phone_number: clientInfo.phone_number.replace(/\s+/, ''),
				});

				if(productInfo && productInfo.requestAddress === true) {
					setUserAddressInfo({
						delivery_title: clientInfo.delivery_title,
						delivery_phone: clientInfo.delivery_phone,
						delivery_address: clientInfo.delivery_address,
						delivery_city: clientInfo.delivery_city,
						delivery_district: clientInfo.delivery_district,
						delivery_owner: clientInfo.delivery_owner,
					})
				}
			})
		}
	}

	const submit = (formData) => {
		(productInfo ? submitWithProducts : submitWithoutProducts)(formData);
	}

	const checkUserDataAddressImportAvailability = () => {
		if(productInfo) {
			formElement.current.serialize().then((fields) => {
				if(fields.client_executive_name && fields.client_executive_surname && fields.client_phone_number) {
					setUserDataAddressImportAvailable(true);
				}
				else {
					setUserDataAddressImportAvailable(false);
				}
			});
		}
		else {
			setUserDataAddressImportAvailable(false);
		}
	}

	const importUserDataToAddress = () => {
		formElement.current.serialize().then((fields) => {
			setUserAddressInfo({
				...userAddressInfo,
				delivery_phone: fields.client_phone_number ? fields.client_phone_number.replace('+9', '') : '',
				delivery_owner: fields.client_executive_name && fields.client_executive_surname ? `${fields.client_executive_name} ${fields.client_executive_surname}`: '',
			})
		})
	}

	const checkAddressInvoiceImportAvailability = () => {
		if(productInfo) {
			formElement.current.serialize().then((fields) => {
				if(fields.client_delivery_address && fields.client_delivery_owner && selectedCity && selectedDistrict) {
					setAddressInvoiceImportAvailable(true);
					setInvoiceInfoTabOpen(true);
				}
				else {
					setAddressInvoiceImportAvailable(false);
				}
			});
		}
		else {
			setAddressInvoiceImportAvailable(false);
		}
	}

	const importAddressToInvoice = () => {
		formElement.current.serialize().then((fields) => {
			setSelectedInvoiceCity(selectedCity);
			setSelectedInvoiceDistrict(selectedDistrict);
			setTimeout(() => {
				const input1 = document.querySelector('textarea[name="client_invoice_address"]');
				const oldVal1 = input1.value;
				const input2 = document.querySelector('input[name="client_company_name"]');
				const oldVal2 = input2.value;

				input1.value = fields.client_delivery_address;
				input2.value = fields.client_delivery_owner;

				let event1 = new Event('input', { bubbles: true })
				event1.simulated = true;
				let tracker1 = input1._valueTracker;
				if (tracker1) {
					tracker1.setValue(oldVal1);
				}
				input1.dispatchEvent(event1);

				let event2 = new Event('input', { bubbles: true })
				event2.simulated = true;
				let tracker2 = input2._valueTracker;
				if (tracker2) {
					tracker2.setValue(oldVal2);
				}
				input2.dispatchEvent(event2);
			}, 250)
		})
	}

	const calculatePlanPayments = useCallback(debounce((enabled, periodMethod, periodCount, startDate, dueDate, orderTotal, periodTime, periodTimeRange) => {
		if (_mounted) {
			if(enabled && periodMethod && periodCount && periodTime && periodTimeRange && startDate && dueDate && orderTotal) {
				setOrderPlanDataLoading(true);
				orderServices.calculateOrderPlan(
					periodTime.value,
					periodTimeRange,
					(periodMethod === 'dynamic' ? 1 : periodCount),
					formatDate(startDate, 'yyyy-MM-dd', { parsed: true }),
					formatDate(dueDate, 'yyyy-MM-dd', { parsed: true }),
					orderTotal.replace(/\./g, '').replace(',', '.')
				).then((payload) => {
					setOrderPlanData(payload);
				}).catch(() => {
					setOrderPlanData(false);
				}).finally(() => {
					setOrderPlanDataLoading(false);
				})
			}
			else {
				setOrderPlanDataLoading(false);
				setOrderPlanData(false);
			}

		}
	}, 500, { leading: false }), []);

	useEffect(() => {
		calculatePlanPayments(
			orderPlanEnabled,
			orderPlanPeriodMethod,
			orderPlanPeriodCount,
			orderStartDate,
			orderDueDate,
			orderTotal,
			orderPlanPeriodTime,
			orderPlanPeriodTimeRange,
		);
	}, [
		orderPlanEnabled,
		orderPlanPeriodMethod,
		orderPlanPeriodCount,
		orderStartDate,
		orderDueDate,
		orderTotal,
		orderPlanPeriodTime,
		orderPlanPeriodTimeRange,
		calculatePlanPayments
	])

	useEffect(() => {
		if(orderPlanEnabled) {
			setShowOrderPlanTab(true);
		}
	}, [orderPlanEnabled])

	useEffect(() => {
		if(orderStartDate && orderDueDate) {
			let diff = compareAsc(orderStartDate, orderDueDate);
			if(diff === 1) {
				setOrderDueDate(false);
			}
		}
	}, [orderStartDate, orderDueDate])

	useEffect(() => {
		const getExchangeTypes = (clientCode = false) => {
			companyServices.listExchangeTypes(clientCode ? {client_code: clientCode} : undefined).then((payload) => {
				let results = payload.map((type) => {
					return {
						...type,
						value: type.id,
						label: type.name,
					};
				})

				// results.unshift(defaultExchangeType);
				setOrderExchangeType(defaultExchangeType);
				setExchangeTypes(results);
			}).catch(() => {
				setExchangeTypes([defaultExchangeType]);
			})
		}

		if(activeClient) {
			getExchangeTypes(activeClient.client_code);

			if (activeClient.client_code !== 'anonim' && productInfo && productInfo.requestAddress === true) {
				companyServices.getClient(activeClient.client_code).then((clientInfo) => {
					setUserAddressInfo({
						delivery_title: clientInfo.delivery_title,
						delivery_phone: clientInfo.delivery_phone,
						delivery_address: clientInfo.delivery_address,
						delivery_city: clientInfo.delivery_city,
						delivery_district: clientInfo.delivery_district,
						delivery_owner: clientInfo.delivery_owner,
					})
				})
			}
		}
		else {
			getExchangeTypes();
		}
		if(!productInfo) {
			setUserInfoTabOpen(false);
		}
	}, [activeClient, clientID, productInfo])

	useEffect(() => {
		if(clientID) {
			companyServices.getClient(clientID).then((client) => {
				client.phone_number = client.phone_number.replace(/\s+/, '');
				setActiveClient(client);
			}).catch(() => {
				close();
			})
		}
	}, [clientID, close]);

	useEffect(() => {
		if(activeClient && cities) {
			if(activeClient.invoice_city) {
				setSelectedInvoiceCity(cities.find(c => c.name.toString() === activeClient.invoice_city.toString()));
			}
			else {
				setSelectedInvoiceCity(null);
			}
		}
	}, [activeClient, cities])

	useEffect(() => {
		if(activeClient && invoiceDistricts) {
			if(activeClient.invoice_district) {
				setSelectedInvoiceDistrict(invoiceDistricts.find(c => c.name.toString() === activeClient.invoice_district.toString()));
			}
			else {
				setSelectedInvoiceDistrict(null);
			}
		}
	}, [activeClient, invoiceDistricts])

	useEffect(() => {
		setActiveCompanyPaymentInstallment(null);
	}, [activeCompanyPaymentType.value]);

	useEffect(() => {
		if(userAddressInfo && cities) {
			if(userAddressInfo.delivery_city) {
				setSelectedCity(cities.find(c => c.name.toString() === userAddressInfo.delivery_city.toString()));
			}
			else {
				setSelectedCity(null);
			}
		}
	}, [userAddressInfo, cities])

	useEffect(() => {
		if(userAddressInfo && districts) {
			if(userAddressInfo.delivery_district) {
				setSelectedDistrict(districts.find(c => c.name.toString() === userAddressInfo.delivery_district.toString()));
			}
			else {
				setSelectedDistrict(null);
			}
		}
	}, [userAddressInfo, districts])

	useEffect(() => {
		if(productInfo) {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			checkAddressInvoiceImportAvailability();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCity, selectedDistrict, productInfo]);

	useEffect(() => {
		_mounted.current = true;

		return(() => {
			_mounted.current = false;
		})
	})

	return (
		<InputForm
			ignoreEmpty
			ref={formElement}
			onSubmit={submit}
			onValidationChange={validationChange}
			className={props.className}>
			<div className="modal-header dark">
				<h1 className="header-title">
					{headingTitle}
				</h1>
				{props.closeBtn}

				{(!clientID && !productInfo) &&
				<>
					<div className="order-clientselect">
						<div className="clientselect-form">
							<FormInput
								hideAsterisk hideError
								disabled={submitting}
								type="select"
								placeholder="Müşteri Seçin"
								label="Müşteri Seçin"
								name="client_search"
								async
								defaultOptions
								onChange={(client) => {
									if(client) {
										client.phone_number = client.phone_number ? client.phone_number.replace(/\s+/, '') : client.phone_number;
									}

									setActiveClient(client);
								}}
								validation={activeClient !== null ? {
									required: 'Bir müşteri seçmelisiniz.'
								} : undefined}
								loadOptions={searchClients}
								value={activeClient}
								className="form-select" />

							<div className="form-controls">
								<span className="controls-seperator">veya</span>

								<Btn
									big={props.mobileState ? undefined : true}
									icon="user-add"
									onClick={resetClient}
									className="controls-newbtn">
									Yeni Müşteri
								</Btn>
							</div>
						</div>
						{(props.userData && props.userData.company.erp_client_api && props.userData.company.erp_client_api.length > 0) &&
						<Btn
							small outline
							icon="search"
							onClick={() => {
								openModal('erp-client-search', {
									top: true,
									onSuccess: (client) => {
										setActiveClient({
											...client,
											value: client.client_code,
											label: client.executive_name + ' ' + client.executive_surname,
										});
									}
								});
							}}
							className="clientselect-erpbtn">
							ERP Müşteri Sorgula
						</Btn>
						}
					</div>
				</>
				}
			</div>

			<div className="modal-content">
				{deliveryTitle &&
				<div className="order-message form-message blue">
					{deliveryTitle}
				</div>
				}
				{today < orderStartDate &&
				<div className="order-message form-message">
					Başlangıç tarihi olarak ileri bir tarihte seçtiğiniz için ödemeniz oluşturulduğu anda aktif olmayacaktır.
				</div>
				}
				{activeClient &&
				<div className="order-clientinfo">
					{activeClient.company_name ? activeClient.company_name : (activeClient.executive_name + ' ' + activeClient.executive_surname)}

					{activeClient.client_code !== 'anonim' &&
					<Btn
						disabled={submitting}
						gray outline
						icon="edit"
						className="clientinfo-cta"
						onClick={() => {
							openModal('client', { top: true, clientID: activeClient.client_code, onSuccess: updateActiveClient });
						}}>
						Müşteri Bilgilerini Düzenle
					</Btn>
					}
				</div>
				}

				{/* <>
				{!(activeClient && activeClient.client_code === 'anonim') &&
					<Collapser
						className="modal-group"
						title="Müşteri Bilgileri"
						// onToggle={(state) => { setUserInfoTabOpen(state) }}
						open={userInfoTabOpen}>
						<div className="grid-container">
							<div className="row">
								<div className="col x8">
									<FormInput
										disabled={activeClient || submitting || clientID}
										type="select"
										z="6"
										label="Müşteri Tipi"
										placeholder="Bir Müşteri Tipi Seçin"
										options={invoiceTypes}
										onChange={(type) => { setOrderInvoiceType(type ? type.value : null); }}
										name="client_invoice_type"
										defaultValue={invoiceTypes[0]}
										value={activeClient ? invoiceTypes[activeClient.invoice_type - 1] : undefined} />
								</div>
							</div>
							<div className="row">
								<div className="col x6">
									<FormInput
										disabled={activeClient || submitting || clientID}
										type="text"
										label={orderInvoiceType === 1 ? 'Müşteri Adı' : 'Yetkili Adı'}
										placeholder={orderInvoiceType === 1 ? 'Müşteri Adı Girin' : 'Yetkili Adı Girin'}
										validation={{
											required: "Müşteri adı girmelisiniz",
										}}
										name="client_executive_name"
										value={activeClient ? activeClient.executive_name : undefined} />
								</div>
								<div className="col x6">
									<FormInput
										disabled={activeClient || submitting || clientID}
										type="text"
										label={orderInvoiceType === 1 ? 'Müşteri Soyadı' : 'Yetkili Soyadı'}
										placeholder={orderInvoiceType === 1 ? 'Müşteri Soyadı Girin' : 'Yetkili Soyadı Girin'}
										validation={{
											required: "Müşteri soyadı girmelisiniz",
										}}
										name="client_executive_surname"
										value={activeClient ? activeClient.executive_surname : undefined} />
								</div>
							</div>
							<div className="row">
								<div className="col x6">
									<FormInput
										disabled={activeClient || submitting || clientID}
										type="email"
										label="E-Posta"
										placeholder="E-Posta Adresi Girin"
										validation={{
											required: "E-posta adresi girmelisiniz",
											email: "Geçerli bir e-posta girmelisiniz",
										}}
										name="client_email"
										value={activeClient ? activeClient.email : undefined} />
								</div>
								<div className="col x6">
									<FormInput
										type="phone"
										disabled={activeClient || submitting || clientID}
										mask="phone"
										label="Cep Telefonu"
										placeholder="Cep Telefonu Girin"
										validation={{
											required: "Cep telefonu girmelisiniz",
											phone: "Geçerli bir telefon numarası girmelisiniz",
										}}
										name="client_phone_number"
										value={activeClient ? activeClient.phone_number : undefined} />
								</div>
							</div>
						</div>
					</Collapser>
				}
				</> */}

				<Collapser
					className="modal-group dark"
					title={`Ödeme Bilgileri${props.mobileState ? ' - ' + orderTotal + ' TL' : ''}`}
					open={!props.mobileState}
					disableToggle={!props.mobileState}>
					<div className="grid-container">
						<div className="row">
							<div className="col x7">
								<FormInput
									disabled={submitting}
									readOnly={productInfo !== false}
									type="number"
									label="Ödeme Tutarı"
									mask="number"
									validation={{
										required: "Ödeme tutarını girmelisiniz"
									}}
									decimals={2}
									onChange={(val) => { setOrderTotal(val); }}
									value={orderTotal}
									name="order_total_amount" />
							</div>
							<div className="col x4">
								<FormInput
									disabled={submitting}
									readOnly={productInfo !== false}
									type="select"
									z="5"
									defaultValue={currencyTypes[0]}
									label="Para Birimi"
									value={orderCurrency}
									onChange={(cur) => { setOrderCurrency(cur); }}
									options={currencyTypes}
									name="order_price_type_id" />
							</div>
						</div>
						{(orderCurrency && orderCurrency.value !== "1") &&
						<React.Fragment>
							<div className="row lined">
								<div className="col x12">
									<FormInput
										disabled={productInfo !== false || submitting}
										type="checkbox"
										checked={orderCurrencyExchangeEnabled}
										onChange={(val) => { setOrderCurrencyExchangeEnabled(val ? true : false); }}
										value={1}
										label="Ödeme anında döviz kuru ile çevrilir ve Türk Lirası olarak ödenir" />
								</div>
							</div>
							{orderCurrencyExchangeEnabled &&
							<div className="row">
								<div className="col x8 no-gap-top">
									<FormInput
										disabled={submitting}
										type="select"
										placeholder="Çevirme Yöntemi Seçin"
										label="Döviz Çevirme Yöntemi"
										z="4"
										defaultOption={defaultExchangeType}
										validation={{
											required: 'Bir döviz çevirme yöntemi seçin',
										}}
										onChange={(type) => { setOrderExchangeType(type); }}
										options={exchangeTypes}
										name="order_currency_exchange_type_id"
										value={orderExchangeType} />

									{(orderExchangeType && orderExchangeType.currency && !orderExchangeType.currency[orderCurrency.code]) &&
									<p className="order-conversionnote">Ödeme hesaplamaları gireceğiniz kur üzerinden hesaplanacaktır.</p>
									}
								</div>
								{orderExchangeType &&
								<div className="col x4 no-gap-top">
									<FormInput
										disabled={submitting || (!(orderExchangeType.code === 'Company' && props.userData && props.userData.is_owner) && (orderExchangeType.currency && orderExchangeType.currency[orderCurrency.code]))}
										type="number"
										decimals={2}
										label="Mevcut Kur"
										placeholder="Kur Girin"
										mask="number"
										value={(orderExchangeType.currency && orderExchangeType.currency[orderCurrency.code]) ? formatNumber(orderExchangeType.currency[orderCurrency.code]) : undefined}
										validation={{
											required: "Döviz kuru girmelisiniz"
										}}
										name="order_currency_exchange_rate" />
								</div>
								}
							</div>
							}
						</React.Fragment>
						}
						<div className="row lined">
							<div className={`col ${productInfo === false && !expressOrder ? 'x4' : 'x12'}`}>
								<FormInput
									disabled={submitting || !groups}
									isSearchable
									type="select"
									z="3"
									className="order-store-code-input"
									readOnly={groups.length < 1 || (productInfo !== false && productInfo.targetGroup)}
									defaultOption={(groups && groups.length === 1) ? groups[0] : undefined}
									label="Ödeme Grubu"
									validation={{
										required: "Bir ödeme grubu seçmelisiniz."
									}}
									placeholder="Bir ödeme grubu seçin"
									options={groups}
									value={selectedGroup}
									name="order_store_code" />
								{productInfo && groups.length === 0 &&
								<div className="form-note error">
									Sepetteki ürün adetlerinin bulunduğu herhangi bir mağaza bulunmuyor.
								</div>
								}
							</div>
							{productInfo === false && !expressOrder &&
							<>
								<div className="col x4">
									<FormInput
										disabled={submitting}
										type="date"
										z="2"
										label="Başlangıç Tarihi"
										placeholder="Başlangıç Tarihi Seçin"
										onChange={(value) => { setOrderStartDate(value); }}
										value={orderStartDate}
										name="order_start_date" />
								</div>
								<div className="col x4">
									<FormInput
										disabled={submitting}
										type="date"
										z="1"
										label="Son Ödeme Tarihi"
										placeholder="Son Ödeme Tarihi Seçin"
										minDate={orderStartDate ? orderStartDate : undefined}
										onChange={(value) => { setOrderDueDate(value); }}
										value={orderDueDate}
										name="order_due_date" />
								</div>
							</>
							}
						</div>
						{productInfo === false &&
						<div className="row">
							<div className="col x10">
								<FormInput
									disabled={submitting}
									type="textarea"
									label="Açıklama"
									rows={3}
									name="order_description" />
							</div>
						</div>
						}
					</div>
				</Collapser>

				{!(activeClient && activeClient.client_code === 'anonim') &&
				<Collapser
					className="modal-group"
					title="Müşteri Bilgileri"
					// onToggle={(state) => { setUserInfoTabOpen(state) }}
					open={userInfoTabOpen}>
					<div className="grid-container">
						<div className="row">
							<div className="col x8">
								<FormInput
									disabled={activeClient || submitting || clientID}
									type="select"
									z="6"
									label="Müşteri Tipi"
									placeholder="Bir Müşteri Tipi Seçin"
									options={invoiceTypes}
									onChange={(type) => { setOrderInvoiceType(type ? type.value : null); }}
									name="client_invoice_type"
									defaultValue={invoiceTypes[0]}
									value={activeClient ? invoiceTypes[activeClient.invoice_type - 1] : undefined} />
							</div>
							{/* <div className="col x4">
									<FormInput
										disabled={activeClient || submitting || clientID}
										readOnly
										type="select"
										z="5"
										label="Serbest Ödeme Özelliği"
										placeholder="Seçiniz"
										options={customOrderTypes}
										name="client_custom_order"
										defaultValue={customOrderTypes[0]}
										value={activeClient ? customOrderTypes[activeClient.custom_order === 1 ? 0 : 1] : undefined} />
								</div> */}
						</div>
						<div className="row">
							<div className="col x6">
								<FormInput
									disabled={activeClient || submitting || clientID}
									type="text"
									label={orderInvoiceType === 1 ? 'Müşteri Adı' : 'Yetkili Adı'}
									placeholder={orderInvoiceType === 1 ? 'Müşteri Adı Girin' : 'Yetkili Adı Girin'}
									validation={{
										required: "Müşteri adı girmelisiniz",
									}}
									onChange={checkUserDataAddressImportAvailability}
									name="client_executive_name"
									value={activeClient ? activeClient.executive_name : undefined} />
							</div>
							<div className="col x6">
								<FormInput
									disabled={activeClient || submitting || clientID}
									type="text"
									label={orderInvoiceType === 1 ? 'Müşteri Soyadı' : 'Yetkili Soyadı'}
									placeholder={orderInvoiceType === 1 ? 'Müşteri Soyadı Girin' : 'Yetkili Soyadı Girin'}
									validation={{
										required: "Müşteri soyadı girmelisiniz",
									}}
									onChange={checkUserDataAddressImportAvailability}
									name="client_executive_surname"
									value={activeClient ? activeClient.executive_surname : undefined} />
							</div>
						</div>
						<div className="row">
							<div className="col x6">
								<FormInput
									disabled={activeClient || submitting || clientID}
									type="email"
									label="E-Posta"
									placeholder="E-Posta Adresi Girin"
									validation={{
										required: "E-posta adresi girmelisiniz",
										email: "Geçerli bir e-posta girmelisiniz",
									}}
									name="client_email"
									value={activeClient ? activeClient.email : undefined} />
							</div>
							<div className="col x6">
								<FormInput
									type="phone"
									disabled={activeClient || submitting || clientID}
									mask="phone"
									label="Cep Telefonu"
									placeholder="Cep Telefonu Girin"
									validation={{
										required: "Cep telefonu girmelisiniz",
										phone: "Geçerli bir telefon numarası girmelisiniz",
									}}
									onChange={checkUserDataAddressImportAvailability}
									name="client_phone_number"
									value={activeClient ? activeClient.phone_number : undefined} />
							</div>
						</div>
					</div>
				</Collapser>
				}

				{(productInfo !== false && productInfo.requestAddress === true) &&
				<Collapser
					open
					className="modal-group"
					title="Adres Bilgileri">
					<div className="grid-container">
						{userDataAddressImportAvailable &&
						<div className="row">
							<div className="col x12 al-right">
								<Btn onClick={importUserDataToAddress}>
									Müşteri Bilgilerinden İçeri Aktar
								</Btn>
							</div>
						</div>
						}
						<div className="row">
							<div className="col x10">
								<FormInput
									disabled={submitting}
									type="text"
									mask="phone"
									label="Telefon Numarası"
									validation={{
										required: 'Bir telefon numarası girmelisiniz.'
									}}
									value={userAddressInfo.delivery_phone}
									name="client_delivery_phone" />
							</div>
						</div>
						<div className="row">
							<div className="col x10">
								<FormInput
									disabled={submitting}
									type="text"
									label="Teslimat Yapılacak Kişi"
									validation={{
										required: 'Teslimat yapılacak kişiyi girmelisiniz.'
									}}
									onChange={checkAddressInvoiceImportAvailability}
									value={userAddressInfo.delivery_owner}
									name="client_delivery_owner" />
							</div>
						</div>
						<div className="row">
							<div className="col x6 m-x6">
								<FormInput
									isSearchable
									disabled={submitting}
									type="select"
									label="İl"
									options={cities}
									validation={{
										required: 'Bir şehir seçmelisiniz.'
									}}
									value={selectedCity}
									onChange={setSelectedCity}
									name="client_delivery_city" />
							</div>
							<div className="col x6 m-x6">
								<FormInput
									isSearchable
									disabled={submitting || !districts}
									type="select"
									label="İlçe"
									options={districts}
									validation={{
										required: 'Bir ilçe seçmelisiniz.'
									}}
									value={selectedDistrict}
									onChange={setSelectedDistrict}
									name="client_delivery_district" />
							</div>
						</div>
						{/* <div className="row">
								<div className="col x10">
									<FormInput
										disabled={submitting}
										type="text"
										label="Adres İsmi"
										validation={{
											required: 'Adresiniz için bir isim girmelisiniz.'
										}}
										value={userAddressInfo.delivery_title}
										name="client_delivery_title" />
								</div>
							</div> */}
						<div className="row">
							<div className="col x10">
								<FormInput
									disabled={submitting}
									type="textarea"
									label="Açık Adres"
									rows={3}
									validation={{
										required: 'Bir adres girmelisiniz.'
									}}
									onChange={checkAddressInvoiceImportAvailability}
									value={userAddressInfo.delivery_address}
									name="client_delivery_address" />
							</div>
						</div>
					</div>
				</Collapser>
				}

				{!expressOrder &&
				<>
					<Collapser
						className="modal-group"
						title="Fatura Bilgileri"
						open={invoiceInfoTabOpen}>
						<div className="grid-container">
							{addressInvoiceImportAvailable &&
							<div className="row">
								<div className="col x12 al-right">
									<Btn onClick={importAddressToInvoice}>
										Adres Bilgilerinden İçeri Aktar
									</Btn>
								</div>
							</div>
							}
							<div className="row">
								{/* <div className="col x6">
										<FormInput
											disabled={(activeClient && activeClient.client_code) || submitting}
											type="text"
											value={activeClient ? activeClient.client_code : undefined}
											label="Cari Kodu"
											validation={productInfo ? {
												required: 'Bir cari kodu girmelisiniz.'
											} : undefined}
											name="client_code" />
									</div> */}
								<div className="col x6">
									<FormInput
										disabled={submitting || activeClient}
										// disabled={(activeClient && activeClient.company_name) || submitting}
										type="text"
										value={activeClient ? activeClient.company_name : undefined}
										label="Fatura Ünvanı"
										validation={productInfo ? {
											required: 'Bir şirket adı girmelisiniz.'
										} : undefined}
										name="client_company_name" />
								</div>
							</div>
							{orderInvoiceType === 2 &&
							<div className="row">
								<div className="col x6">
									<FormInput
										key="client_tax_office"
										disabled={(activeClient && activeClient.tax_office) || submitting}
										type="text"
										value={activeClient ? activeClient.tax_office : undefined}
										label="Vergi Dairesi"
										validation={productInfo ? {
											required: 'Bir vergi dairesi girmelisiniz.'
										} : undefined}
										name="client_tax_office" />
								</div>
								<div className="col x6">
									<FormInput
										key="client_tax_number"
										disabled={(activeClient && activeClient.tax_number) || submitting}
										type="text"
										value={activeClient ? activeClient.tax_number : undefined}
										label="Vergi No"
										validation={productInfo ? {
											required: 'Bir vergi numarası girmelisiniz.'
										} : undefined}
										name="client_tax_number" />
								</div>
							</div>
							}
							{/*
									:
									<div className="row">
										<div className="col x10">
											<FormInput
												key="client_identity_number"
												disabled={(activeClient && activeClient.identity_number) || submitting}
												type="number"
												value={activeClient ? activeClient.identity_number : undefined}
												maxLength="11"
												label="TC Kimlik No"
												validation={productInfo ? {
													required: 'Bir kimlik numarası girmelisiniz.',
												} : undefined}
												name="client_identity_number" />
										</div>
									</div>
								}*/}
							{/* <div className="row">
									<div className="col x10">
										<FormInput
											disabled={(activeClient && activeClient.invoice_name) || submitting}
											type="text"
											value={activeClient ? activeClient.invoice_name : undefined}
											label="Fatura İsmi"
											validation={productInfo ? {
												required: 'Bir fatura ismi girmelisiniz.'
											} : undefined}
											name="client_invoice_name" />
									</div>
								</div> */}
							<div className="row">
								<div className="col x10">
									<FormInput
										disabled={(activeClient && activeClient.invoice_address) || submitting}
										type="textarea"
										value={activeClient ? activeClient.invoice_address : undefined}
										label="Fatura Adresi"
										rows={3}
										validation={productInfo ? {
											required: 'Bir fatura adresi girmelisiniz.'
										} : undefined}
										name="client_invoice_address" />
								</div>
							</div>
							<div className="row">
								<div className="col x6 m-x6">
									<FormInput
										isSearchable
										disabled={(activeClient && activeClient.invoice_city && selectedInvoiceCity) || submitting}
										type="select"
										label="İl"
										options={cities}
										validation={productInfo ? {
											required: 'Bir şehir seçmelisiniz.'
										} : undefined}
										value={selectedInvoiceCity}
										onChange={setSelectedInvoiceCity}
										name="client_invoice_city" />
								</div>
								<div className="col x6 m-x6">
									<FormInput
										isSearchable
										disabled={(activeClient && activeClient.invoice_district && selectedInvoiceDistrict) || submitting || !invoiceDistricts}
										type="select"
										label="İlçe"
										options={invoiceDistricts}
										validation={productInfo ? {
											required: 'Bir ilçe seçmelisiniz.'
										} : undefined}
										value={selectedInvoiceDistrict}
										onChange={setSelectedInvoiceDistrict}
										name="client_invoice_district" />
								</div>
							</div>
						</div>
					</Collapser>

					{(productInfo === false && props.template === false) &&
					<>
						<Collapser
							className="modal-group order-plan"
							title="Periyodik Ödeme"
							open={showOrderPlanTab}>
							<div className="collapser-section">
								<FormInput
									disabled={submitting}
									type="checkbox"
									checked={orderPlanEnabled}
									onChange={(val) => { setOrderPlanEnabled(val ? true : false); }}
									value={1}
									name="periodic_payment_enabled"
									label="Periyodik Ödeme Tanımla" />
							</div>
							{orderPlanEnabled &&
							<div className="collapser-section">
								<div className="portlet gray  plan-interval">
									<div className="portlet-header">
										<strong className="portlet-title">Tekrar Aralığı</strong>
									</div>
									<div className="portlet-section bottom interval-inputwrap">
										<span>Her</span>
										<FormInput
											disabled={submitting}
											className="interval-range"
											type="number"
											value={orderPlanPeriodTimeRange}
											onChange={(val) => { setOrderPlanPeriodTimeRange(val); }}
											name="order_period_time_range" />
										<FormInput
											disabled={submitting}
											className="interval-period"
											type="select"
											value={orderPlanPeriodTime}
											onChange={(val) => { setOrderPlanPeriodTime(val); }}
											options={orderPlanPeriodTimes}
											name="order_period_time" />
									</div>
								</div>
								<div className="portlet gray  plan-installments">
									<div className="portlet-header">
										<strong className="portlet-title">Taksit Adedi</strong>
									</div>
									<div className="portlet-section bottom">
										<div className="grid-container">
											<div className="row">
												<div className="col x12">
													<FormInput
														disabled={submitting}
														type="radio"
														name="plan_period_method"
														value={orderPlanPeriodMethod}
														onChange={(val) => { setOrderPlanPeriodMethod(val); }}
														options={[
															{
																value: 'static',
																label: 'Elle Gir',
															},
															{
																value: 'dynamic',
																label: 'Son ödeme tarihine göre otomatik hesapla',
															},
														]} />
												</div>
											</div>
											{orderPlanPeriodMethod === 'static' &&
											<div className="row">
												<div className="col x8 installments-inputwrap">
													<span>Talebi</span>
													<FormInput
														disabled={submitting}
														type="number"
														value={orderPlanPeriodCount}
														onChange={(val) => { setOrderPlanPeriodCount(val); }}
														className="installments-count"
														name="order_period_count" />
													<span>ödemeye böl.</span>
												</div>
											</div>
											}
										</div>
									</div>
								</div>
								<div className="portlet plan-data loader-container">
									<Loader loading={orderPlanDataLoading} />
									<div className="portlet-header border-bottom">
										<strong className="portlet-title">Plan Önizlemesi</strong>
									</div>
									<div className="portlet-section bottom">
										{orderPlanData ?
											<Table
												emptyMessage="Bir hata oluştu."
												className="data-table"
												heading={
													<React.Fragment>
														<th>Dönem</th>
														<th>Ödeme Tarihi</th>
														<th>Tutar</th>
													</React.Fragment>
												}>
												{orderPlanData.map((data, nth) => (
													<TR key={nth} /*mobileTitle={nth + 1 + '. Talep'}*/>
														<TD>
															{nth +1}. Dönem
														</TD>
														<TD>
															{formatDate(data.date, 'd MMMM yyyy')}
														</TD>
														<TD>
															<Pricebox
																price={data.price}
																currency={orderCurrency.symbol} />
														</TD>
													</TR>
												))}
											</Table>
											:
											<div className="alert warning center">
												<div className="alert-content">
													Periyodik ödeme planını tanımlamak için ödeme tutarını ve periyot bilgilerini girmelisiniz.
												</div>
											</div>
										}
									</div>
								</div>
							</div>
							}
						</Collapser>
						<Collapser
							className="modal-group"
							title="Detaylı Sipariş Bilgileri">
							<div className="grid-container">
								<div className="row">
									<div className="col x6">
										<FormInput
											disabled={submitting}
											type="text"
											label="İşletme Sipariş Kodu"
											name="order_business_code" />
									</div>
									<div className="col x6">
										<FormInput
											disabled={submitting}
											type="text"
											label="Fatura No"
											name="order_invoice_no" />
									</div>
								</div>
								<div className="row">
									<div className="col x12">
										<FormInput
											disabled={submitting}
											type="select"
											z="2"
											name="order_company_payment_type_id"
											placeholder="Sabitlemek için bir ödeme aracı seçin"
											label="Ödeme Aracını Sabitle"
											async
											defaultOptions
											onChange={setActiveCompanyPaymentType}
											loadOptions={searchCompanyPaymentTypes}
											value={activeCompanyPaymentType} />
									</div>
								</div>
								{activeCompanyPaymentType && activeCompanyPaymentType.value !== 0 &&
								<React.Fragment>
									<div className="row">
										<div className="col x12">
											<FormInput
												disabled={submitting}
												type="checkbox"
												checked={companyPaymentInstallmentSelectionEnabled}
												onChange={(val) => { setCompanyPaymentInstallmentSelectionEnabled(val ? true : false); }}
												value={1}
												label="Taksit seçeneklerini sınırla" />
										</div>
									</div>
									{companyPaymentInstallmentSelectionEnabled &&
									<div className="row">
										<div className="col x12 no-gap-top">
											<FormInput
												key={activeCompanyPaymentType.value}
												disabled={submitting}
												type="select"
												name="order_company_payment_installment_ids"
												placeholder="Taksit seçeneklerini seçin"
												label="Taksitler"
												async
												defaultOptions
												multiple
												onChange={setActiveCompanyPaymentInstallment}
												loadOptions={(search) => {
													return searchCompanyPaymentInstallments(activeCompanyPaymentType.value, search);
												}}
												validation={{
													required: "En az bir taksit seçeneği seçmelisiniz.",
												}}
												value={activeCompanyPaymentInstallment} />
										</div>
									</div>
									}
								</React.Fragment>
								}
							</div>
						</Collapser>
					</>
					}
				</>
				}
			</div>

			<div className="modal-controls">
				<Btn
					nude
					disabled={submitting}
					onClick={close}>
					Vazgeç
				</Btn>
				<Btn
					loading={submitting}
					disabled={submitting || groups?.length === 0}
					type="submit">
					Oluştur
				</Btn>
			</div>
		</InputForm>
	)
}

const ConnectedModal = connect(mapStateToProps)(CreateOrderModal);

ConnectedModal.defaultProps = {
	className: '',
	containerClass: 'modal-create-or-update-order mobile-full',
	name: 'create-order',
	title: false,
	content: false,
	source: false,
	template: false,
	clientID: false,
	periodicPayment: false,
	productInfo: false,
	orderPlan: false,
	express: false,
}

export default ConnectedModal
